import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  Save,
  Cancel,
  Edit,
  Warning,
  CheckCircle,
  FilterList,
  Refresh,
} from "@mui/icons-material";
import {departmentStockService} from "../../services/departmentStockService";
import { useSnackbar } from "notistack";

export const StockLevelManager = () => {
  const [departmentStocks, setDepartmentStocks] = useState([]);
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [editedLevels, setEditedLevels] = useState({});
  const [bulkUpdateDialog, setBulkUpdateDialog] = useState(false);
  const [bulkMinLevel, setBulkMinLevel] = useState("");
  const [bulkMaxLevel, setBulkMaxLevel] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    departmentType: "",
  });

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchDepartmentStocks();
  }, [lowStockOnly, filters]);

  const fetchDepartmentStocks = async () => {
    try {
      setLoading(true);
      let response;

      if (lowStockOnly) {
        response = await departmentStockService.getLowStockDepartments({
          ...filters,
        });
      } else {
        response = await departmentStockService.getDepartmentStocks({
          ...filters,
          limit: 100, // Get more records for bulk management
        });
      }

      const stocksData =
        response.data.data?.departmentStocks ||
        response.data.data?.lowStockDepartments ||
        response.data.data ||
        [];

      setDepartmentStocks(stocksData);
      setEditedLevels({});
      setSelectedStocks([]);
    } catch (error) {
      enqueueSnackbar("Error fetching department stocks", { variant: "error" });
      console.error("Error fetching department stocks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedStocks(departmentStocks.map((stock) => stock._id));
    } else {
      setSelectedStocks([]);
    }
  };

  const handleSelectStock = (stockId) => {
    setSelectedStocks((prev) => {
      if (prev.includes(stockId)) {
        return prev.filter((id) => id !== stockId);
      } else {
        return [...prev, stockId];
      }
    });
  };

  const handleLevelChange = (stockId, field, value) => {
    setEditedLevels((prev) => ({
      ...prev,
      [stockId]: {
        ...prev[stockId],
        [field]: value === "" ? "" : Number(value),
      },
    }));
  };

  const handleSaveStock = async (stockId) => {
    const levels = editedLevels[stockId];
    if (!levels) return;

    // Validation
    if (levels.minimumLevel < 0 || levels.maximumLevel < 0) {
      enqueueSnackbar("Levels cannot be negative", { variant: "error" });
      return;
    }

    if (
      levels.maximumLevel &&
      levels.minimumLevel &&
      levels.minimumLevel >= levels.maximumLevel
    ) {
      enqueueSnackbar("Minimum level must be less than maximum level", {
        variant: "error",
      });
      return;
    }

    try {
      setSaving(true);
      await departmentStockService.updateStockLevels(stockId, levels);
      enqueueSnackbar("Stock levels updated successfully", {
        variant: "success",
      });

      // Update local state
      setDepartmentStocks((prev) =>
        prev.map((stock) =>
          stock._id === stockId ? { ...stock, ...levels } : stock
        )
      );

      // Clear edited levels for this stock
      setEditedLevels((prev) => {
        const newLevels = { ...prev };
        delete newLevels[stockId];
        return newLevels;
      });
    } catch (error) {
      enqueueSnackbar("Error updating stock levels", { variant: "error" });
      console.error("Error updating stock levels:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedStocks.length === 0) {
      enqueueSnackbar("Please select stocks to update", { variant: "warning" });
      return;
    }

    // Validation
    const minLevel = Number(bulkMinLevel);
    const maxLevel = Number(bulkMaxLevel);

    if (bulkMinLevel !== "" && minLevel < 0) {
      enqueueSnackbar("Minimum level cannot be negative", { variant: "error" });
      return;
    }

    if (bulkMaxLevel !== "" && maxLevel < 0) {
      enqueueSnackbar("Maximum level cannot be negative", { variant: "error" });
      return;
    }

    if (
      bulkMinLevel !== "" &&
      bulkMaxLevel !== "" &&
      minLevel >= maxLevel
    ) {
      enqueueSnackbar("Minimum level must be less than maximum level", {
        variant: "error",
      });
      return;
    }

    try {
      setSaving(true);
      const updateData = {};
      if (bulkMinLevel !== "") updateData.minimumLevel = minLevel;
      if (bulkMaxLevel !== "") updateData.maximumLevel = maxLevel;

      // Update all selected stocks
      const updatePromises = selectedStocks.map((stockId) =>
        departmentStockService.updateStockLevels(stockId, updateData)
      );

      await Promise.all(updatePromises);

      enqueueSnackbar(
        `Successfully updated ${selectedStocks.length} stock level(s)`,
        { variant: "success" }
      );

      // Refresh data
      await fetchDepartmentStocks();
      setBulkUpdateDialog(false);
      setBulkMinLevel("");
      setBulkMaxLevel("");
    } catch (error) {
      enqueueSnackbar("Error updating stock levels", { variant: "error" });
      console.error("Error updating stock levels:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = (stockId) => {
    setEditedLevels((prev) => {
      const newLevels = { ...prev };
      delete newLevels[stockId];
      return newLevels;
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "success",
      depleted: "error",
      expired: "warning",
    };
    return colors[status] || "default";
  };

  const isLowStock = (stock) => {
    return stock.quantity <= stock.minimumLevel && stock.status === "active";
  };

  const getCurrentValue = (stock, field) => {
    return editedLevels[stock._id]?.[field] ?? stock[field] ?? "";
  };

  const hasChanges = (stockId) => {
    return editedLevels[stockId] !== undefined;
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Stock Level Manager
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchDepartmentStocks}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant={editMode ? "outlined" : "contained"}
            startIcon={editMode ? <Cancel /> : <Edit />}
            onClick={() => {
              setEditMode(!editMode);
              if (editMode) {
                setEditedLevels({});
              }
            }}
          >
            {editMode ? "Cancel Edit Mode" : "Edit Mode"}
          </Button>
          {editMode && selectedStocks.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setBulkUpdateDialog(true)}
              disabled={saving}
            >
              Bulk Update ({selectedStocks.length})
            </Button>
          )}
        </Box>
      </Box>

      {/* Low Stock Alert Banner */}
      {departmentStocks.some((stock) => isLowStock(stock)) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            {departmentStocks.filter((stock) => isLowStock(stock)).length} stock
            allocation(s) are below minimum level
          </Typography>
        </Alert>
      )}

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="depleted">Depleted</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Department Type</InputLabel>
                <Select
                  value={filters.departmentType}
                  onChange={(e) =>
                    handleFilterChange("departmentType", e.target.value)
                  }
                  label="Department Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="production">Production</MenuItem>
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="administration">Administration</MenuItem>
                  <MenuItem value="research">Research</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant={lowStockOnly ? "contained" : "outlined"}
                startIcon={<FilterList />}
                onClick={() => setLowStockOnly(!lowStockOnly)}
              >
                {lowStockOnly ? "Show All" : "Low Stock Only"}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary" align="center">
                Total: {departmentStocks.length} records
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stock Levels Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {editMode && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        selectedStocks.length === departmentStocks.length &&
                        departmentStocks.length > 0
                      }
                      indeterminate={
                        selectedStocks.length > 0 &&
                        selectedStocks.length < departmentStocks.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell>Alert</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Item</TableCell>
                <TableCell align="right">Current Qty</TableCell>
                <TableCell align="right">Min Level</TableCell>
                <TableCell align="right">Max Level</TableCell>
                <TableCell>Status</TableCell>
                {editMode && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {departmentStocks.map((stock) => (
                <TableRow
                  key={stock._id}
                  sx={{
                    "&:hover": { bgcolor: "action.hover" },
                    bgcolor: isLowStock(stock) ? "warning.light" : "inherit",
                  }}
                >
                  {editMode && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedStocks.includes(stock._id)}
                        onChange={() => handleSelectStock(stock._id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Tooltip
                      title={
                        isLowStock(stock)
                          ? "Low Stock Warning"
                          : stock.status === "depleted"
                          ? "Depleted"
                          : stock.status === "expired"
                          ? "Expired"
                          : "Normal"
                      }
                    >
                      {isLowStock(stock) ? (
                        <Warning color="warning" />
                      ) : stock.status === "depleted" ? (
                        <Warning color="error" />
                      ) : stock.status === "expired" ? (
                        <Warning color="warning" />
                      ) : (
                        <CheckCircle color="success" />
                      )}
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {stock.department?.departmentName || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stock.department?.departmentCode || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {stock.item?.name || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stock.item?.brand || ""} • {stock.item?.unit || ""}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={isLowStock(stock) ? "warning.main" : "inherit"}
                    >
                      {stock.quantity}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {editMode ? (
                      <TextField
                        size="small"
                        type="number"
                        value={getCurrentValue(stock, "minimumLevel")}
                        onChange={(e) =>
                          handleLevelChange(
                            stock._id,
                            "minimumLevel",
                            e.target.value
                          )
                        }
                        sx={{ width: 100 }}
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                      />
                    ) : (
                      <Typography variant="body2">
                        {stock.minimumLevel || 0}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {editMode ? (
                      <TextField
                        size="small"
                        type="number"
                        value={getCurrentValue(stock, "maximumLevel")}
                        onChange={(e) =>
                          handleLevelChange(
                            stock._id,
                            "maximumLevel",
                            e.target.value
                          )
                        }
                        sx={{ width: 100 }}
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                      />
                    ) : (
                      <Typography variant="body2">
                        {stock.maximumLevel || "N/A"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stock.status}
                      size="small"
                      color={getStatusColor(stock.status)}
                    />
                  </TableCell>
                  {editMode && (
                    <TableCell align="center">
                      {hasChanges(stock._id) ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSaveStock(stock._id)}
                            disabled={saving}
                          >
                            <Save />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="default"
                            onClick={() => handleCancelEdit(stock._id)}
                            disabled={saving}
                          >
                            <Cancel />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No changes
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* No Results */}
      {!loading && departmentStocks.length === 0 && (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No department stock records found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lowStockOnly
                ? "No low stock alerts at this time"
                : "Department stock records will appear here once items are issued to departments"}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Bulk Update Dialog */}
      <Dialog
        open={bulkUpdateDialog}
        onClose={() => !saving && setBulkUpdateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Update Stock Levels</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Update minimum and maximum stock levels for {selectedStocks.length}{" "}
            selected stock allocation(s). Leave fields empty to keep current
            values.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Minimum Level"
                type="number"
                value={bulkMinLevel}
                onChange={(e) => setBulkMinLevel(e.target.value)}
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: (
                    <InputAdornment position="start">Min:</InputAdornment>
                  ),
                }}
                helperText="Set the minimum stock level for all selected items"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Maximum Level"
                type="number"
                value={bulkMaxLevel}
                onChange={(e) => setBulkMaxLevel(e.target.value)}
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: (
                    <InputAdornment position="start">Max:</InputAdornment>
                  ),
                }}
                helperText="Set the maximum stock level for all selected items"
              />
            </Grid>
          </Grid>

          {bulkMinLevel !== "" &&
            bulkMaxLevel !== "" &&
            Number(bulkMinLevel) >= Number(bulkMaxLevel) && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Minimum level must be less than maximum level
              </Alert>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUpdateDialog(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkUpdate}
            variant="contained"
            disabled={
              saving ||
              (bulkMinLevel === "" && bulkMaxLevel === "") ||
              (bulkMinLevel !== "" &&
                bulkMaxLevel !== "" &&
                Number(bulkMinLevel) >= Number(bulkMaxLevel))
            }
          >
            {saving ? <CircularProgress size={24} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
